import * as React from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeElementsOptions } from "@stripe/stripe-js";

import { useAPI } from "kw/api/useAPI";
import { useThemeContext } from "kw/themeContext";

const stripeApiKey = globalThis?.settings?.STRIPE_API_KEY;

type TStripeRoot = {
  stripe?: PromiseLike<Stripe | null> | Stripe | null;
  children: unknown;
  product?: object;
};

const StripeRoot = ({ children, product }: TStripeRoot) => {
  const { context } = useThemeContext();

  const theme = context.theme;
  const { query } = useAPI();
  const [stripeObject, setStripeObject] = React.useState<Promise<Stripe | null> | null>(
    null
  );

  function fetchStripeConfig(productKey: string) {
    query(
      {
        stripeConfig: [
          {
            productKey: productKey,
          },
          {
            account: true,
          },
        ],
      },
      (response: { stripeConfig: { account: string } }) => {
        const account = response.stripeConfig.account;
        if (account) {
          const stripeWithAccount = stripeApiKey
            ? loadStripe(stripeApiKey, {
                stripeAccount: account,
              })
            : null;
          setStripeObject(stripeWithAccount);
        } else {
          const stripeNoAccount = stripeApiKey ? loadStripe(stripeApiKey) : null;
          setStripeObject(stripeNoAccount);
        }
      }
    );
  }

  React.useEffect(() => {
    const productKey = product?.key;
    if (productKey) {
      fetchStripeConfig(productKey);
    }
  }, [product]);

  // The amount is set here to initialize the PaymentElement
  // It is not locked and will be determined by the PaymentIntent on the server.
  const options: StripeElementsOptions = {
    mode: "payment",
    amount: 100,
    currency: "aud",
    paymentMethodCreation: "manual", // required
    paymentMethodTypes: ["card"],
    appearance: {
      theme: "flat",
      variables: {
        fontFamily: theme?.typography.DisplayMedium.fontFamily,
        fontSizeBase: theme?.typography.font300.fontSize,
        colorPrimary: theme?.colors.primary,
        colorText: theme?.colors.primary,
        colorDanger: theme?.colors.negative,
        borderRadius: theme?.borders.radius200,
      },
      rules: {
        ".Input:focus": {
          borderColor: theme?.colors.primary,
          boxShadow: `0 0 0 2px ${theme?.colors.primary}`,
        },
      },
    },
  };

  return (
    <Elements stripe={stripeObject} options={options}>
      {children}
    </Elements>
  );
};

export { StripeRoot };
